import TextGrid from "overprint/overprint/text-grid";
import Font from "overprint/overprint/font";
import Cell from "overprint/overprint/cell";
import { plotLine } from "flatland/src/line";
import Glyphs from "../assets/objects/glyphs";
import Palette from "../palette";
import { Commands } from "../engine/commands";
import Input from "../engine/input";
import Settings from "../engine/settings";

const UIGlyphs = {
  LookCursor: Cell("⊗", Palette.HihiBlack, Palette.TakapuLightGrey),
  TargetCursorBlank: Cell("~", Palette.TakahēDarkBlue, Palette.KōtareLightBlue),
  TargetCursorTrail: Cell("~", Palette.TakahēDarkBlue, Palette.KōtareLightBlue),
  TargetCursorValid: Cell("~", Palette.TakahēDarkBlue, Palette.KōtareLightBlue),
  TargetCursorBlocked: Cell("~", Palette.TakahēDarkBlue, Palette.KōtareLightBlue)
}

const glyphCache = new Map();

const InputMode = {
  Exploring: 0,
  Looking: 1,
  Targeting: 2,
  Equipping: 3
}

class AbstractCursor {
  constructor(position) {
    this.x = position.x;
    this.y = position.y;
  }
}

class TargetingCursor extends AbstractCursor {
  isValidTarget = false;

  handleMovement(direction, stage) {
    const tx = this.x + direction.x;
    const ty = this.y + direction.y;

    // TODO: modulate by weapon distance
    if (stage.isVisible(tx, ty) && !(tx == stage.player.x && ty == stage.player.y)) {
      this.x = tx;
      this.y = ty;
    }

    this.isValidTarget = stage.canMoveTo(tx, ty);
  }
}

class LookingCursor extends AbstractCursor {
  selectEntity(position, stage) {
    this.selectedEntity = true;
    this.x = position.x;
    this.y = position.y;
  }

  handleMovement(direction, stage) {
    const tx = this.x + direction.x;
    const ty = this.y + direction.y;

    if (stage.isSeen(tx, ty) && !(tx == stage.player.x && ty == stage.player.y)) {
      this.x = tx;
      this.y = ty;
    }

    if (stage.entitiesAt(this.x, this.y).length > 0) {
      this.selectedEntity = true;
    } else {
      this.selectedEntity = false;
    }
  }
}

class GameScreen {
  constructor() {
    this.width = 80;
    this.height = 50;
    this.canvas = document.querySelector(".game-stage");
    
    this.camera = {
      x: 0,
      y: 0
    }
    this.mode = InputMode.Exploring;
    this.lastMove = Commands.MoveUp;
    this.rescale();
  }

  activate(context) {
    document.body.classList.remove("is-title");
    this.rescale();
  }

  deactivate(context) {
    return;
  }

  handleInput(input, stage) {
    
    switch(input) {
      // case "p":
      //   console.log(`Location: {x: ${stage.player.x}, y: ${stage.player.y}}`);
      //   console.log(stage.player);
      // break;
      // case "1":
      //   return Commands.ShowLog;
      // break;
      case Input.Backquote:
      case Input.Dead:
        return Commands.ShowDebug;
      break;

      case Input.Look:
        if (this.mode == InputMode.Exploring) {
          this.activateLookingMode(stage);
        } else {
          this.deactivateCursorMode();
        }
      break;

      case Input.FocusRight:
        if (this.mode == InputMode.Looking) {
          const nearby = stage.visibleEntities();

          if (nearby.length == 0) {
            return;
          } else if (nearby.length == 1) {
            this.cursor.x = nearby[0].x;
            this.cursor.y = nearby[0].y;
          }
        }
        // .sort((a, b) => {
        //   const gt = manhattanDistance({x: stage.player.x, y: stage.player.y}, {x: a.x, y: a.y});
        //   manhattanDistance({x: stage.player.x, y: stage.player.y}, {x: b.x, y: b.y});
        // });
      break;
      // case "t":
      // case "f":
      //   this.activateTargetingMode(stage);
      // break;
      case Input.Ok:
        if (this.mode == InputMode.Looking) {
          return this.lookCommand(stage);
        }
      break;

      case Input.Cancel:
        if (this.mode == InputMode.Looking) {
          this.deactivateCursorMode();
        } else {
          return this.pauseCommand();
        }
      break;

      case Input.MoveUp: return this.handleMovement(Commands.MoveUp, stage);
      case Input.MoveDown: return this.handleMovement(Commands.MoveDown, stage);
      case Input.MoveLeft: return this.handleMovement(Commands.MoveLeft, stage);
      case Input.MoveRight: return this.handleMovement(Commands.MoveRight, stage);
      case Input.Rest: return Commands.Rest;
      case Input.Open: return this.openCommand(stage);
      case Input.Close: return this.closeCommand(stage);
      case Input.Utility:
        const tryOpen = this.openCommand(stage);
        if (tryOpen) {
          return tryOpen;
        } else {
          return this.closeCommand(stage);
        }
    }
    return false;
  }

  attackRangedTargetCommand(stage) {
    this.mode = InputMode.Exploring;
    return {
      ...Commands.RangedAttack,
      detail: {
        target: {
          x: this.cursor.x,
          y: this.cursor.y
        }
      }
    }
  }

  pauseCommand() {
    return {
      ...Commands.Pause,
      detail: {}
    }
  }

  getCommand(stage) {
    const pile = stage.entitiesAt(stage.player.x, stage.player.y).filter(entity => entity.isItem());

    if (pile.length < 1) return false;

    return {
      ...Commands.Get,
      detail: pile
    }
  }

  dropCommand(stage) {
    return {
      ...Commands.Drop,
      detail: {
        slot: null,
        index: 0
      }
    }
  }

  openCommand(stage) {
    const doors = stage.adjacentPointsMatchingTile(stage.player.x, stage.player.y, t => t.isOpenable);
    
    if (doors.length == 0) return false;
    
    return {
      ...Commands.Open,
      detail: {
        target: doors[0]
      }
    }
  }

  closeCommand(stage) {
    const doors = stage.adjacentPointsMatchingTile(stage.player.x, stage.player.y, t => t.isClosable);
    
    if (doors.length == 0) return false;
    
    return {
      ...Commands.Close,
      detail: {
        target: doors[0]
      }
    }
  }

  lookCommand(stage, x=false, y=false) {
    // TODO: position dialog on screen by tile location
    // screen: this.cellToPx(
    //   this.cursor.x - this.camera.x,
    //   this.cursor.y - this.camera.y
    // )
    const mx = (x === false) ? this.cursor.x : x + this.camera.x;
    const my = (y === false) ? this.cursor.y : y + this.camera.y;

    if (stage.isSeen(mx, my)) {
      return {
        ...Commands.Examine,
        detail: stage.lookAt(mx, my)
      }
    }
  }

  cellToPx(rowPos, colPos) {
    // TODO: really should not be reaching into the internals of the grid class
    // const cellWidth = this.grid._cellWidth * this.grid._ratio;
    // const cellHeight = this.grid._cellHeight * this.grid._ratio;
    const cellWidth = this.grid._cellWidth;
    const cellHeight = this.grid._cellHeight;
    const x = Math.round(rowPos * cellWidth);
    const y = Math.round(colPos * cellHeight);
    return { x, y, cellWidth, cellHeight }
  }

  handleMovement(command, stage) {
    switch (this.mode) {
      case InputMode.Exploring:
        this.lastMove = command;
        return command;
      break;
      case InputMode.Looking:
      case InputMode.Targeting:
        this.cursor.handleMovement(command, stage);
        return false;
      break;
    }
  }

  activateTargetingMode(stage) {
    if (stage.player.isRangedWeaponEquipped()) {
      this.mode = InputMode.Targeting;

      this.cursor = new TargetingCursor({
        x: stage.player.x + this.lastMove.x,
        y: stage.player.y + this.lastMove.y
      });
    }
  }

  activateLookingMode(stage, location=false) {
    this.mode = InputMode.Looking;

    const cursorPosition = (location) ? location : {
      x: stage.player.x + this.lastMove.x,
      y: stage.player.y + this.lastMove.y
    };

    this.cursor = new LookingCursor(cursorPosition);
  }

  deactivateCursorMode() {
    this.mode = InputMode.Exploring;
    this.cursor = false;
  }

  // Aspect ratio hardcoded, see CSS
  //
  // 20 : 13 (40 : 26)
  // standard screen:
  //    w 960 : 480 = 1440 (75%, 25%)
  //    h 624
  get viewportFontSize() {
    //return Math.round(this.viewportWidth / 90 * 1.5 + 1);
    return Math.round(this.viewportWidth / 96);
  }

  rescale() {
    this.viewportWidth = document.documentElement.clientWidth;
    this.grid = new TextGrid(this.canvas, {
      width: this.width,
      height: this.height,
      emptyCell: Glyphs.Void,
      font: Font("JuliaMono", false, this.viewportFontSize)
    });
  }
  
  handleClick(ev, stage) {
    // TODO: extract target/DOM check into interface input layer
    if (ev.target == this.canvas) {
      if (!Settings.usePointerNav) return;

      this.deactivateCursorMode();

      const [sx, sy] = this.grid.pxToCell(ev);

      const x = sx + this.camera.x;
      const y = sy + this.camera.y;

      if (stage.isSeen(x, y) && stage.canMoveTo(x, y)) {
        // TODO: support autoexplore through commands
        this.autoExplore = { x, y };
      }
    }
  }

  handleRightClick(ev, stage) {
    // TODO: extract target/DOM check into interface input layer
    if (ev.target == this.canvas) {
      // Don't do any looking if the setting is disabled
      if (!Settings.usePointerNav) return;

      // Compute look target
      const [x, y] = this.grid.pxToCell(ev);
      const target = { x: x + this.camera.x, y: y + this.camera.y };

      // Don't look at or select the player
      if (target.x == stage.player.x && target.y == stage.player.y) return;

      // Activate look cursor
      this.activateLookingMode(stage, target);

      // Force a render to ensure that the cursor gets rendered without waiting for the popover
      this.render(stage);

      // Return the command to process
      return this.lookCommand(stage, x, y);
    }
  }

  updateCamera(x, y, mapWidth, mapHeight) {
    let offsetX = Math.floor(x - (this.width / 2));
    offsetX = Math.min(offsetX, mapWidth - this.width);
    offsetX = Math.max(0, offsetX);

    let offsetY = Math.floor(y - (this.height / 2));
    offsetY = Math.min(offsetY, mapHeight - this.height);
    offsetY = Math.max(0, offsetY);

    // TODO: this should be a rect rather than a point
    this.camera = {
      x: offsetX,
      y: offsetY
    }
  }

  renderMap(stage) {
    // TODO: move this somewhere more suitable
    if (this.autoExplore) {
      stage.player.activateAutoExplore(stage, this.autoExplore);
      this.autoExplore = false;
    }

    for (let y=0; y < this.height; y++) {
      for (let x=0; x < this.width; x++) {
        let tileType = Glyphs.Empty;
        let cx = x + this.camera.x;
        let cy = y + this.camera.y;

        if (stage.isVisible(cx, cy)) {
          tileType = Glyphs[stage.map[cy][cx].name];
        } else if (stage.isSeen(cx, cy)) {
          tileType = Glyphs.UNLIT[stage.map[cy][cx].name];
        }

        this.grid.writeCell(x, y, tileType);
      }
    }
  }

  renderMapMuteSeen(stage) {
    for (let y=0; y < this.height; y++) {
      for (let x=0; x < this.width; x++) {
        let tileType = Glyphs.Void;
        let cx = x + this.camera.x;
        let cy = y + this.camera.y;

        // Hardcoded FOV code until tile lighting is decided
        if (stage.isVisible(cx, cy)) {
          try {
            tileType = Glyphs[stage.map[cy][cx].name];
          } catch(err) {
            console.log(stage.map)
            console.log(stage.map[cy])
            console.log(stage.map[cy][cx])
            console.log(cy, cx)
            throw new Error("bad name")
          }
          tileType = Glyphs[stage.map[cy][cx].name];

          const occupants = stage.entitiesAt(cx, cy);

          if (occupants.length == 1) {
            tileType = this.overprintEntity(occupants[0], tileType)
          } else if (occupants.length > 1) {
            let entity = occupants.find(ent => ent.isBlocking());
            // TODO: represent piles rather than draw the topmost item
            if (!entity) entity = occupants[occupants.length - 1];
            tileType = this.overprintEntity(entity, tileType);
          }

        } else if (stage.isSeen(cx, cy)) {
          if (stage.inOverworld()) {
            try {
              tileType = Glyphs[stage.map[cy][cx].name];
            } catch(err) {
              console.log(stage.map)
              console.log(stage.map[cy])
              console.log(stage.map[cy][cx])
              console.log(cy, cx)
              throw new Error("bad name")
            }
            tileType = Glyphs[stage.map[cy][cx].name];
          } else {
            // Hack to mute tiles outside FOV in underworld
            //
            // Need to fix Overprint to be much more explicit about comparing
            // cells by value equality vs
            //
            // This doesn’t provide much of an improvement to the double-buffer
            // rendering architecture but does prevent a lot of GC churn through
            // avoiding creating lots of little duplicate objects.
            let glyph = Glyphs[stage.map[cy][cx].name];

            let glyphCacheKey = `${glyph.character}|${glyph.foregroundColor}|${Glyphs.Void.backgroundColor}`;

            if (!glyphCache.has(glyphCacheKey)) {
              tileType = Cell(
                glyph.character,
                glyph.foregroundColor,
                Glyphs.Void.backgroundColor
              );
              glyphCache.set(glyphCacheKey, tileType);
            } else {
              tileType = glyphCache.get(glyphCacheKey);
            }
          }
        }

        this.grid.writeCell(x, y, tileType);
      }
    }
  }

  overprintEntity(entity, tile) {
    let glyph = entity.glyph;
    if (glyph.backgroundColor != tile.backgroundColor) {
      // Need to rebind the object id to blow the ref cache
      glyph = Cell(
        glyph.character,
        glyph.foregroundColor,
        tile.backgroundColor
      );
    }
    return glyph;
  }

  renderEntities(stage) {
    for (let entity of stage.entities) {
      let cx = entity.x - this.camera.x;
      let cy = entity.y - this.camera.y;
      if (stage.isVisible(entity.x, entity.y)) {
        let glyph = entity.glyph;
        let tile = Glyphs[stage.map[entity.y][entity.x].name];
        if (glyph.backgroundColor != tile.backgroundColor) {
          // Need to rebind the object id to blow the ref cache
          glyph = Cell(
            glyph.character,
            glyph.foregroundColor,
            tile.backgroundColor
          );
        }
        this.grid.writeCell(cx, cy, glyph);
      }
    }
  }

  renderDebug(stage) {
    let xline = `x: ${stage.player.x}`.split("");
    let yline = `y: ${stage.player.y}`.split("");

    let xpos = 0;
    for (let xchar of xline) {
      this.grid.writeCell(xpos, 0, Cell(
        xchar,
        Glyphs.Ocean.foregroundColor
      ));
      xpos++;
    }

    let ypos = 0;
    for (let ychar of yline) {
      this.grid.writeCell(ypos, 1, Cell(
        ychar,
        Glyphs.Ocean.foregroundColor
      ));
      ypos++;
    }

    let label = "Health:".split("");
    let hpos = 0;
    for (let hchar of label) {
      this.grid.writeCell(hpos, 2, Cell(
        hchar,
        Glyphs.Ocean.foregroundColor
      ));
      hpos++;
    }

    this.grid.writeCell(8, 2, Cell(
      stage.player.health.hp.toString(),
      Glyphs.Ocean.foregroundColor
    ));
  }

  drawLookCursor(stage) {
    const cursorGlyph = UIGlyphs.LookCursor;
    const target = stage.lookAt(this.cursor.x, this.cursor.y);

    if (target.glyph !== undefined) {
      cursorGlyph.character = target.glyph.character;
    } else {
      cursorGlyph.character = Glyphs[target.name].character;
    }

    this.grid.writeCell(
      this.cursor.x - this.camera.x,
      this.cursor.y - this.camera.y,
      cursorGlyph
    );
  }

  renderCursor(stage) {
    if (this.mode == InputMode.Looking) {
      this.drawLookCursor(stage);
    } else if (this.mode == InputMode.Targeting) {
      // TODO: should cursor store vec?
      const targetPos = {
        x: this.cursor.x,
        y: this.cursor.y
      }

      if (!this.cursor.isValidTarget) {
        this.grid.writeCell(
          targetPos.x - this.camera.x,
          targetPos.y - this.camera.y,
          UIGlyphs.TargetCursorBlank
        );
        return;
      }

      const playerPos = {
        x: stage.player.x,
        y: stage.player.y
      }

      const [, ...targetTrail] = plotLine(playerPos, targetPos);
      let hasClearLos = true;

      for (const trail of targetTrail) {
        if (stage.isPartiallyCovered(trail.x, trail.y)) {
          hasClearLos = false;
          break;
        }

        let glyph = UIGlyphs.TargetCursorTrail;
        let tile = Glyphs[stage.map[trail.y][trail.x].name];
        if (glyph.backgroundColor != tile.backgroundColor) {
          // Need to rebind the object id to blow the ref cache
          glyph = Cell(
            glyph.character,
            glyph.foregroundColor,
            tile.backgroundColor
          );
        }

        this.grid.writeCell(
          trail.x - this.camera.x,
          trail.y - this.camera.y,
          glyph
        );
      }

      this.grid.writeCell(
        targetPos.x - this.camera.x,
        targetPos.y - this.camera.y,
        hasClearLos ? UIGlyphs.TargetCursorValid : UIGlyphs.TargetCursorBlocked
      );
    }
  }

  render(stage) {
    if (stage.isLocked()) return;

    this.updateCamera(
      stage.player.x,
      stage.player.y,
      stage.width,
      stage.height
    );

    // See if this reduces pixel artifacts
    //this.grid._canvas.width = this.grid._canvas.width;

    this.renderMap(stage);

    this.renderEntities(stage);

    this.renderCursor(stage);

    this.grid.render();

    // Redraw the HUD (this should probably not run on frameloop but meh)
    //this.hud.render(stage);
  }
}

export default GameScreen;
